import { useState, useEffect } from "react";
import { Box, Typography, Button, ButtonGroup } from "@mui/material";
import AdminFamilySelect from "components/creation/AdminFamilySelect";
import PersonalizedTasks from "components/tasks/PersonalizedTasks";
import CalendarTasks from "components/member/home/CalendarTasks";
import { Family } from "protogen/advisors_service_pb";
import FlexPage from "components/layout/FlexPage";
import { useSearchParams } from "react-router-dom";
import { useGetFamilyDetails } from "services/advisor";
import { useGetFamilyCalendarIntegrations } from "services/calendar";
import FlexBox from "components/common/FlexBox";
import useIsMobile from "components/hooks/useIsMobile";

type ComponentType = "personalized" | "calendar";

export default () => {
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFamily, setSelectedFamily] = useState<Family | null>(null);
  const [activeComponent, setActiveComponent] =
    useState<ComponentType>("personalized");

  const { request: getFamilyDetails } = useGetFamilyDetails();
  const {
    request: getFamilyCalendarIntegrations,
    data: calendarIntegrationsData,
    loading: calendarIntegrationsLoading,
  } = useGetFamilyCalendarIntegrations();

  // Load family from URL on initial mount and when familyRef changes
  useEffect(() => {
    const familyRef = searchParams.get("familyRef");
    if (familyRef) {
      const loadFamily = async () => {
        const response = await getFamilyDetails({ familyRef });
        if (response?.family) {
          setSelectedFamily(response.family);
        }
      };
      loadFamily();
    } else {
      setSelectedFamily(null);
    }
  }, [searchParams.get("familyRef")]);

  // Load calendar integrations when family changes
  useEffect(() => {
    if (selectedFamily?.ref) {
      getFamilyCalendarIntegrations({
        familyRef: selectedFamily.ref,
      });
    }
  }, [selectedFamily?.ref]);

  const handleFamilyChange = (
    familyRef: string | null,
    family?: Family | null,
  ) => {
    setSelectedFamily(family || null);

    // Update URL query parameter
    if (familyRef) {
      setSearchParams({ familyRef });
    } else {
      setSearchParams({});
    }
  };

  const integrationCount =
    calendarIntegrationsData?.importIntegrations?.length || 0;
  const isLoadingIntegrations = calendarIntegrationsLoading;

  return (
    <FlexPage>
      <Box display="flex" flexDirection="column" gap="32px" maxWidth="650px">
        <Typography variant="display">Client AI Tester</Typography>

        <Box>
          <Typography variant="h3" sx={{ marginBottom: "16px" }}>
            Select a Family
          </Typography>
          <AdminFamilySelect
            selectedFamily={selectedFamily?.ref || null}
            onChange={handleFamilyChange}
            title="Select Family"
          />
        </Box>

        <Box>
          <Typography variant="h3" sx={{ marginBottom: "16px" }}>
            Select Component
          </Typography>
          <ButtonGroup
            sx={{
              display: "flex",
              ...(isMobile && {
                marginBottom: "16px",
                width: "100%",
              }),
            }}
          >
            <Button
              sx={{ flex: "1", width: "100%" }}
              variant={
                activeComponent === "personalized"
                  ? "buttonGroupSelected"
                  : "buttonGroup"
              }
              onClick={() => setActiveComponent("personalized")}
              disabled={!selectedFamily}
            >
              Personalized Tasks
            </Button>
            <Button
              sx={{ flex: "1", width: "100%" }}
              variant={
                activeComponent === "calendar"
                  ? "buttonGroupSelected"
                  : "buttonGroup"
              }
              onClick={() => setActiveComponent("calendar")}
              disabled={!selectedFamily}
            >
              Calendar Tasks
            </Button>
          </ButtonGroup>
        </Box>

        {selectedFamily ? (
          <FlexBox variant="contained" direction="column">
            {activeComponent === "personalized" ? (
              <PersonalizedTasks
                family={selectedFamily}
                showWishlistButton={false}
                integrationCount={integrationCount}
                forceLoading={isLoadingIntegrations}
              />
            ) : (
              <CalendarTasks
                family={selectedFamily}
                integrationCount={integrationCount}
                forceLoading={isLoadingIntegrations}
              />
            )}
          </FlexBox>
        ) : (
          <Box
            sx={{
              padding: "24px",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              backgroundColor: "#FAFAFA",
              textAlign: "center",
            }}
          >
            <Typography variant="body" color="#666666">
              Select a family above to test AI components
            </Typography>
          </Box>
        )}
      </Box>
    </FlexPage>
  );
};
